import {
  Appointments,
  AppointmentStatus,
  AppointmentType,
  BnplFeePayer,
  BooksStatus,
  BusinessPlanType,
  CustomerEmergencyContacts,
  CustomerGuests,
  Customers,
  Employees,
  EmployeesToBusinesses,
  InactiveEmployeesToBusinesses,
  Payee,
  PaymentMethod,
  PaymentStatus,
  PayrollType,
  PiercingLocations,
  ProviderCommissionType,
  QueueSettings,
  RecipientUserRelation,
  ReferralSources,
  Roles,
  Users,
  VirtualMeetingProvider,
  WaiverRequests,
} from '@prisma/client';
import React, { SetStateAction } from 'react';
import { FormikHelpers, FormikValues } from 'formik';
import { convertToRaw } from 'draft-js';
import { FullPayment, PaymentsGroup } from './types/payments';
import { OrderWithPayments } from './types/orders';
import { FlashbookDesign } from './types/flashbooks';
import type { OfferedSlot } from './types/appointments';

export interface Appointment {
  appointmentId: string;
  employeeId?: string;
  customerId: string;
  businessId?: string;
  status: AppointmentStatus;
  selectedArtists?: string[];
  previousStatus?: AppointmentStatus;
  onschedId?: string;
  tattooLocation?: string;
  bodySide?: string;
  tattooDetails?: string;
  tattooSize?: string;
  tattooColor?: string;
  tattooInspiration?: string;
  prefTattooStyle?: string;
  isFirstTattoo?: boolean;
  isNewCustomer?: boolean;
  refImgsId?: string[];
  areaImgsId?: string[];
  budget?: number;
  skinTone?: string;
  otherNotes?: string;
  rejectionMessage?: string;
  priceType?: string;
  minPrice?: number;
  maxPrice?: number;
  hourlyPrice?: number;
  price?: number;
  appointmentLengthInMinutes?: number;
  depositPrice?: number;
  depositPaidOn?: string;
  rescheduledByCust?: number;
  rescheduledByEmployee?: number;
  previousStartDateTime?: string;
  previousEndDateTime?: string;
  customDesignMessage?: string;
  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  startDateTime?: string;
  endDateTime?: string;
  tripCity?: string;
  timezone?: string;
  employee?: User;
  customer: User;
  depositCheckoutSessionActive: boolean;
  checkoutSessionActive: boolean;
  checkoutSessionCompleted: boolean;
  checkoutCompletedOn?: string;
  tipAmt: number;
  appointmentType: AppointmentType;
  serviceCategory?: string;
  acceptedOn?: Date;
  offeredSlots?: OfferedSlot[];
  offeredTimesDeclinedAt?: string | null;
  rejectedOn?: Date;
  canceledOn?: Date;
  scheduledOn?: Date;
  confirmedOn?: string;
  modifiedBy?: string;
  paymentStatus?: string;
  serviceId?: number;
  piercingLocationId?: number;
  paymentMethod?: PaymentMethod;
  depositPaymentMethod?: PaymentMethod;
  reschedulingFeesPaid?: number;
  reschedulingFeesRefundAmount?: number;
  bookerUserId?: string;
  createdByUserId?: string;
  canceledByUserId?: string;
  internalNotes?: string;
  wasNoShow?: boolean;
  providerCommissionType?: ProviderCommissionType | null;
  businessTripId?: string;
  waiverRequest?: WaiverRequests | null;
  skipWaiver?: boolean;
  depositAgreementRequest?: {
    id: string;
    signedAt?: string | null;
    signedFileLink?: string | null;
  } | null;
  availability?: string;
  archivedAt?: Date;
  ongoingProject?: boolean;
  coverUp?: boolean;
  tattooServiceType?: string;
  surroundingTattoos?: boolean;
  isEphemeral?: boolean;
  isDepositRefunded?: boolean;
  referralSources?: ReferralSources[];
  isVirtual?: boolean;
  virtualMeetingUrl?: string;
  virtualMeetingProvider?: string;
  joinedQueueAt?: Date | null;
  leftQueueAt?: Date | null;
  flashDesign?: FlashbookDesign;
  customerGuestId?: string | null;
  customerGuest?: Pick<
    CustomerGuests,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'dob'
    | 'email'
    | 'medicalHistory'
    | 'relation'
  > | null;
  isForSomeoneElse?: boolean;
  recipientFirstName?: string;
  recipientLastName?: string;
  recipientDob?: Date;
  recipientUserRelation?: RecipientUserRelation;
}

export interface AppointmentWithCustomerAndEmployee extends Appointments {
  customer: Customers & Users;
  employee: (Employees & Users) | null;
}

export interface GeneralRequest {
  requestId: string;
  customerId: string;
  businessId: string;

  tattooLocation?: string;
  bodySide?: string;
  tattooDetails?: string;
  tattooSize?: string;
  tattooColor?: string;
  tattooInspiration?: string;
  prefTattooStyle?: string;
  isFirstTattoo?: boolean;
  isNewCustomer?: boolean;
  refImgsId?: string[];
  areaImgsId?: string[];
  budget?: number;
  skinTone?: string;
  otherNotes?: string;
  internalNotes?: string;
  availability?: string;
  ongoingProject?: boolean;
  rejectionMessage?: string;
  coverUp?: boolean;
  tattooServiceType?: string;
  surroundingTattoos?: boolean;
  isEphemeral?: boolean;
  selectedArtists?: string[];

  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date;

  customer: User;
  referralSources?: ReferralSources[];

  customerGuestId?: string | null;
  customerGuest?: Pick<
    CustomerGuests,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'dob'
    | 'email'
    | 'medicalHistory'
    | 'relation'
  > | null;
  isForSomeoneElse?: boolean;
  recipientFirstName?: string;
  recipientLastName?: string;
  recipientDob?: Date;
  recipientUserRelation?: RecipientUserRelation;
}

export interface Row {
  rowId: string;
  cells: React.ReactNode[];
}

export interface TabInt {
  id?: string | number;
  label: string;
  component?: React.ReactNode;
  hidden?: boolean;
}

export interface FullTabInt extends TabInt {
  tabId: number;
}

export interface CalendarItem {
  start: Date;
  end: Date;
  customerName: string;
  employeeName: string;
  employeeImg?: string;
  tattooLocation: string | undefined;
  bodySide?: string;
  appointmentId: string;
  button: React.ReactNode;
  editButton: React.ReactNode;
}

export interface CalendarSection {
  day: Date;
  calendarItems: CalendarItem[];
}

export interface Option<
  TValue extends string | number | boolean = string | number | boolean,
> {
  value: TValue;
  label?: string;
}

export interface Input {
  name: string;
  label: string;
  type:
    | 'text'
    | 'number'
    | 'dollar'
    | 'select'
    | 'multiselect'
    | 'textarea'
    | 'image'
    | 'phone'
    | 'scheduler'
    | 'link'
    | 'percent'
    | 'date'
    | 'date-range'
    | 'time'
    | 'color'
    | 'node';
  options?: Option[];
  placeholder?: string;
  disabled?: any;
  hidden?: any;
  prependText?: any;
  helperText?: string;
  // need to add support in specific Input component
  tooltip?: string;
  validateChange?: (value: string) => boolean | Promise<boolean>;
  // Runs after the new value is committed (select only)
  onChange?: (value: string) => void;
  allText?: string;
  autoWidth?: boolean;
  showListWithAll?: boolean;
  hideAllOption?: boolean;
  minDate?: Date;
  displayEmpty?: boolean;
  minutesStep?: number;
  min?: number;
  max?: number;
  step?: number;
  colors?: string[];
  node?: React.ReactNode;
}

export type InputRowType = Input[];

export interface InputGroupType {
  name: string;
  inputRows: InputRowType[];
}

export type FileSectionRow = {
  type: 'FILE';
  value: {
    id: string;
    url: string;
    name: string;
    type: 'image' | 'pdf';
    showName?: boolean;
    actionComponent?: React.ReactNode;
  }[];
};

export type ColorSectionRow = {
  type: 'COLOR';
  value: string[];
};

export type LinkSectionRow = {
  type: 'LINK';
  value: { url: string; label: string }[];
};

export type SectionRow = {
  id?: string;
  label: string;
  action?: () => void;
} & (
  | {
      type: 'IMAGE' | 'TEXT' | 'PHONE' | 'SUBTEXT';
      value: string[] | (string[] | undefined)[];
    }
  | {
      type: 'NODE';
      value: React.ReactNode[];
    }
  | LinkSectionRow
  | FileSectionRow
  | ColorSectionRow
);

export interface Section {
  name: string;
  // Header text when it should differ from `name` (which SideBarSection matches on).
  title?: string;
  rows: SectionRow[];
  values?: any;
  buttonText?: string | React.ReactNode;
  buttonAction?: () => void;
  noGap?: boolean;
  defaultExpanded?: boolean;
  hideHeader?: boolean;
  summary?: string;
  microLabel?: boolean;
}

export interface ModalData {
  name: string;
  sections: Section[];
}

export interface FooterAction {
  label: string;
  action: any;
  coachMark?: React.ReactNode;
}

export interface FooterActionGroup {
  group: string;
  actions: FooterAction[];
}

export interface Employee {
  employeeId: string;
  profileImage: string;
  urlPath: string;
  cancellationPolicyText: string;
  liabilityWaiver: string;
  stackDays: boolean;
  stackTimes: boolean;
  createdAt: string;
  updatedAt: string;
  calendarAuthorized: boolean;
  standardDesignMessage?: string;
  onschedResourceId?: string;
  minAppointmentPrice?: number;
  maxCancellationTimes?: number;
  minCancellationDays?: number;
  bookAheadLimitDays?: number;
  displayName?: string;
  deletedAt?: string;
  calendarEmail?: string;
  calendarAuthUrl?: string;
  about?: string;
  user?: User;
  paymentCustomerId?: string;
  primaryEmployeeToBusinessId?: number;
  booksStatus?: BooksStatus;
  employeesToBusinesses?: [
    {
      id: number;
      employeeId: string;
      businessId: string;
      canViewReports: boolean;
      canManageTeam: boolean;
      canManageWaivers: boolean;
      canIssueRefunds: boolean;
      canManageCampaigns: boolean;
      canViewAllShopCustomers: boolean;
      generalRequestOwner: boolean;
      payrollType: PayrollType;
      paymentToEmployeePercent: number;
      selectedRoles: EmployeesToBusinesses['selectedRoles'];
      isBookable?: boolean;
      isImpersonation?: boolean;
      impersonationExpiresAt?: string | null;
    },
  ];
  inactiveEmployeesToBusinesses?: Array<
    Pick<
      InactiveEmployeesToBusinesses,
      'businessId' | 'customerAccessRevokedAt'
    >
  >;
  restrictedBusinessIds?: string[];
}

export type CustomerEmergencyContact = Pick<
  CustomerEmergencyContacts,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phoneNumber'
  | 'streetAddress'
  | 'streetAddress2'
  | 'city'
  | 'state'
  | 'zip'
>;

export type Customer = Customers & {
  user: Users;
  appointments: Appointments[];
  internalNotes: string | null;
  emergencyContacts: CustomerEmergencyContact[];
};

export interface Service {
  defaultDurationInMinutes: number;
  defaultPrice: number;
  defaultDepositPrice: number | null;
  enableDirectBookings: boolean;
  minimumAgeInMonths: number;
  serviceCategory: string;
  serviceDescription: string;
  serviceName: string;
  serviceId: number;
  sortOrder?: number;
  employeesToServices: {
    employeeId: string;
    serviceId: number;
    serviceCommissionRate?: number | null;
  }[];
  piercingLocations?: PiercingLocations[];
}

export interface EmployeeToBusiness {
  employeeId: string;
  stripeId?: string | null;
  profileImage: string;
  urlPath: string;
  cancellationPolicyText: string;
  liabilityWaiver: string;
  stackDays: boolean;
  stackTimes: boolean;
  createdAt: string;
  updatedAt: string;
  calendarAuthorized: boolean;
  standardDesignMessage?: string;
  onschedResourceId?: string;
  maxCancellationTimes?: number;
  minCancellationDays?: number;
  bookAheadLimitDays?: number;
  displayName?: string;
  deletedAt?: string;
  calendarEmail?: string;
  calendarAuthUrl?: string;
  about?: string;
  user?: User;
  payrollType: PayrollType;
  depositToEmployeePercent: number;
  tipToEmployeePercent: number;
  paymentToEmployeePercent: number;
  isBookable: boolean;
  booksStatus?: BooksStatus;
  bonusCommissionPercent?: number;
  addToAllChats: boolean;
  generalRequestOwner: boolean;
  canViewReports: boolean;
  canManageWaivers: boolean;
  canIssueRefunds: boolean;
  canManageCampaigns: boolean;
  payeeForOtherPayroll: Payee;
  canManageTeam: boolean;
  canViewAllShopCustomers: boolean;
  selectedRoles: EmployeesToBusinesses['selectedRoles'];
  color: string | null;
  sortOrder?: number;
  defaultHourlyRate?: number;
  consultationVirtualByDefault?: boolean;
  consultationPermanentVirtualLink?: string;
  defaultVirtualProvider?: VirtualMeetingProvider;
  isImpersonation?: boolean;
  impersonationExpiresAt?: string | null;
}

export interface Business {
  businessId: string;
  organizationId: string;
  giftCardsEnabled: boolean;
  defaultGiftCardBalance: number;
  name: string;
  urlPath: string;
  coverImage: string;
  thumbnailImage: string;
  city: string;
  state: string;
  zip: string;
  streetAddress: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  streetAddress2?: string;
  services?: Service[];
  defaultTattooDeposit?: number;
  terminalReaderId?: string;
  disableCreditTipping: boolean;
  shopPolicyRaw?: any;
  employeesToBusinesses?: [
    {
      payeeForOtherPayroll: Payee;
      businessId: string;
      employeeId: string;
      employee: Employee;
      payrollType: PayrollType;
      depositToEmployeePercent: number;
      tipToEmployeePercent: number;
      paymentToEmployeePercent: number;
      bonusCommissionPercent?: number;
      isBookable: boolean;
      addToAllChats: boolean;
      canViewReports: boolean;
      canManageWaivers: boolean;
      canIssueRefunds: boolean;
      canManageCampaigns: boolean;
      generalRequestOwner: boolean;
      canManageTeam: boolean;
      canViewAllShopCustomers: boolean;
      selectedRoles: EmployeesToBusinesses['selectedRoles'];
      color: string | null;
      sortOrder?: number;
      defaultHourlyRate?: number;
      queueSettings?: QueueSettings;
    },
  ];
  inactiveEmployeesToBusinesses: Pick<
    InactiveEmployeesToBusinesses,
    'businessId'
  >[];
  inStoreCustomerServiceFeeRate?: number;
  onlineCustomerServiceFeeRate?: number;
  businessTaxRates?: {
    serviceTaxRate: number;
    productTaxRate: number;
  };
  type: BusinessPlanType;
  tattooAftercareLink: string | null;
  reviewLink: string | null;
  allowGeneralRequests: boolean;
  stationMax: number | null;
  businessTrips: Trip[];
  isTerminalPurchased: boolean;
  terminalStripeSubscriptionId?: string;
  terminalSubscriptionPaymentFailed?: boolean;
  isBnplEnabled: boolean;
  bnplFeePayer: BnplFeePayer;
  paymentsRate: number;
  reviewLinksEnabled: boolean;
  queueSettings?: QueueSettings;
  connectedStripeAt?: Date;
  allowMinors?: boolean;
  minimumTattooAgeInYears?: number;
  requireMinorIdentification?: boolean;
  requireMinorBirthCertificate?: boolean;
  requireMinorGuardianIdentification?: boolean;
  requireMinorGuardianPresent?: boolean;
  hasAdsFeature: boolean;
  cashDiscountEnabled: boolean;
  businessWebRedesignEligible?: boolean;
  businessWebRedesignEnabled?: boolean;
  businessWebRedesignLocked?: boolean;
  inventoryEnabled?: boolean;
}

export interface User {
  dob?: string;
  authServiceId: string;
  userId: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: Roles;
  isActive: boolean;
  timezone: string;
  updatedAt: string;
  employee: Employee;
  igHandle?: string;
  deletedAt?: string;
  displayName?: string;
  internalNotes?: string;
  urlPath?: string;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zip?: string;
  emergencyContacts?: CustomerEmergencyContact[];
}

export interface LoadedUser {
  authServiceId: string;
  userId: string;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: Roles;
  isActive: boolean;
  timezone: string;
  updatedAt: string;
  employee: Employee;
  igHandle?: string;
  deletedAt?: string;
  canViewReports: boolean;
  canManageTeam: boolean;
  canManageWaivers: boolean;
  canIssueRefunds: boolean;
  canManageCampaigns: boolean;
  canViewAllShopCustomers: boolean;
  generalRequestOwner: boolean;
  isImpersonation?: boolean;
  inactiveEmployeesToBusinesses: Array<{
    businessId: string;
    customerAccessRevokedAt: Date | string | null;
  }>;
  restrictedBusinessIds: string[];
  business: {
    id: string;
    organizationId: string;
    name: string;
    urlPath: string;
    coverImage: string;
    thumbnailImage: string;
    city: string;
    state: string;
    zip: string;
    streetAddress: string;
    streetAddress2?: string;
    employees: EmployeeToBusiness[];
    services?: Service[];
    defaultTattooDeposit?: number;
    terminalReaderId?: string;
    inStoreCustomerServiceFeeRate?: number;
    onlineCustomerServiceFeeRate?: number;
    businessTaxRates?: {
      serviceTaxRate: number;
      productTaxRate: number;
    };
    type: BusinessPlanType;
    tattooAftercareLink: string | null;
    reviewLink: string | null;
    allowGeneralRequests: boolean;
    stationMax: number | null;
    businessTrips: Trip[];
    disableCreditTipping: boolean;
    inactiveEmployeesToBusinesses: Pick<
      InactiveEmployeesToBusinesses,
      'businessId'
    >[];
    isTerminalPurchased: boolean;
    terminalStripeSubscriptionId?: string;
    terminalSubscriptionPaymentFailed?: boolean;
    isBnplEnabled: boolean;
    bnplFeePayer: BnplFeePayer;
    paymentsRate: number;
    reviewLinksEnabled: boolean;
    shopPolicyRaw?: ReturnType<typeof convertToRaw>;
    shopPolicyHtml?: string;
    queueSettings?: QueueSettings;
    connectedStripeAt?: Date;
    giftCardsEnabled: boolean;
    defaultGiftCardBalance: number;
    allowMinors?: boolean;
    minimumTattooAgeInYears?: number;
    requireMinorIdentification?: boolean;
    requireMinorBirthCertificate?: boolean;
    requireMinorGuardianIdentification?: boolean;
    requireMinorGuardianPresent?: boolean;
    hasAdsFeature: boolean;
    cashDiscountEnabled: boolean;
    businessWebRedesignEligible?: boolean;
    businessWebRedesignEnabled?: boolean;
    businessWebRedesignLocked?: boolean;
    inventoryEnabled?: boolean;
  };
  allBusinesses: Business[];
}

export interface Filter {
  name: string;
  options: FilterOption[];
  state: string[];
  setState: React.Dispatch<SetStateAction<string[]>>;
  emptyLabel?: string;
  allLabel?: string;
}

export interface FilterOption<TValue extends string = string> {
  label: string;
  value: TValue;
  color?: string;
}

export interface AvailableTimes {
  allowableBookings: number;
  allowableCapacity: number;
  availableBookings: number;
  availableCapacity: number;
  date: string;
  displayTime: string;
  duration: number;
  endDateTime: string;
  resourceId: string;
  startDateTime: string;
  time: number;
  travelAppointmentId: string;
  travelTimeMins: number;
}

export interface UnavailableTimes {
  id: string;
  objectName: string;
  locationId: string;
  calendarId: string;
  resourceId: string;
  serviceId: string;
  date: string;
  fromTime: number;
  toTime: number;
  tzOffset: number;
  startDateTime: string;
  endDateTime: string;
  reasonCode: string;
  reason: string;
  entityType: string;
  entityId: number;
  resourceName: string;
  serviceName: string;
}

export interface DayItem {
  available: boolean;
  date: string;
  preferred?: boolean;
}

export interface Trip {
  tripId: string;
  employeeId?: string;
  businessId?: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
  startDateTime: string;
  endDateTime: string;
  booksOpen: Boolean;
  studioName?: string;
  streetAddress?: string;
  streetAddress2?: string;
}

// Type for total sales by date
export interface SalesData {
  date: string;
  sales: number;
}

// Type for total appointments by date
export interface AppointmentsData {
  date: string;
  appointments: number;
}

// Type for total sales by employee
export interface SalesByEmployee {
  artist: string;
  sales: number;
  color?: string;
}

// Type for count of appointments by appointment type
export interface AppointmentsByType {
  appointmentType: string;
  appointments: number;
}

export interface PiercingLocation {
  locationId: number;
  locationCategory: string;
  locationType: string;
}

export interface Transaction {
  id: string;
  amount: number;
  tipAmount?: number;
  dateCreated: Date;
  dateUpdated: Date;
  type: string;
  discountAmount?: number;
  chargeReason?: string;
  status?: PaymentStatus;
  error?: string;
  payment?: FullPayment;
  appointment?: Appointment;
  order?: OrderWithPayments;
  paymentsGroup?: PaymentsGroup;
  customer?: Customer;
}

export interface CheckoutFailureData {
  failureCode: string;
  failureMessage: string;
  status: string;
  type: string;
  paymentIntentId: string;
}

export interface ModalState {
  isOpen: boolean;
  status: 'loading' | 'idle' | 'failed' | 'success';
  title: string;
  message: string;
  buttonText: string;
}

export interface TerminalCheckoutModalState extends ModalState {}

export enum PayoutDistributionOptions {
  COMMISSION = 'COMMISSION',
  BUSINESS = 'BUSINESS',
  EMPLOYEE = 'EMPLOYEE',
  EMPTY = '',
}

export interface FormikForm<Values extends FormikValues = any> {
  validationSchema?: any | (() => any);
  initialValues: Values;
  onSubmit: (
    values: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => void | Promise<any>;
}
